import React, { useEffect, useRef, useState } from "react";
import Carousel from "react-multi-carousel";
import { ButtonGroupProps } from "react-multi-carousel/lib/types";
import Image from "@/components/Atoms/Image";
import "react-multi-carousel/lib/styles.css";
import { useRouter } from "next/router";
import styles from "./index.module.scss";
import { VideoHeroSlider } from "@/components/Molecules/VideoCard";
import { OverlayShadow } from "@/components/Molecules/OverlayShadow/Overlay";
import { FullBackgroundImage, FullBackgroundImageHero } from "@/components/Molecules/ImageCard";
import {
  FeaturedCopyCard,
  SliderCopyCard,
} from "@/components/Molecules/HeroCopyCard";
import { NavigationSlider } from "@/atoms/NavigationSlider";
import { PageScroll } from "@/atoms/PageScroll";
import GeoWrapper, {
  isAvailableForCountry,
  resolveCurrentCountry,
} from "@/components/Templates/Main/GeoConditional";
import { IconGallery } from "@/components/Molecules/IconGallery";
import { Shape } from "@/components/Atoms/Spacer";
import {
  legacyBgColorFields,
  sliderImageBgColorSelection,
} from "@/lib/fragments";
import {
  resolveBackgroundStyle,
  resolvePortfolioBackgroundHex,
  type BackgroundColorField,
} from "@/lib/resolve-background-color";
import { isCmsBooleanEnabled, variants } from "@/lib/functions";
import { LazyMotion, domAnimation, m } from "framer-motion";
import {
  getCarouselIndexForSlide,
  getSlideIndexFromCarousel,
} from "./carouselSlideIndex";

const imageOverlayColor = { hex: "#000" } as const;

/** Full-bleed hero image layer — sits outside the copy container. */
const sliderFullBleedImageClass =
  "absolute inset-0 z-0 overflow-hidden [&>div]:!h-full [&>div]:!w-full";
const AUTOPLAY_INTERVAL_MS = 5000;
/** Wait for react-multi-carousel infinite clones before advancing. */
const AUTOPLAY_INIT_DELAY_MS = 800;

function DefaultContainSlideImage({
  data,
}: {
  data: SliderImageSlide["image"];
}) {
  const [imageLoading, setImageLoading] = useState(false);

  if (!data) return null;

  return (
    <LazyMotion features={domAnimation}>
      <m.div
        variants={variants("")}
        initial="hidden"
        whileInView={imageLoading ? "visible" : undefined}
        viewport={{ once: true }}
        exit="exit"
        key={data.id ?? String(data.responsiveImage?.src ?? "slide-image")}
        className={styles.defaultContainImageAsset}
      >
        <Image
          data={data}
          layout="fill"
          objectFit="cover"
          onLoad={() => setImageLoading(true)}
        />
      </m.div>
    </LazyMotion>
  );
}

interface ColorField {
  hex: string;
  cssRgb?: string;
}

interface ColorWrapper {
  color: ColorField;
}

interface ResponsiveImageData {
  id?: string;
  responsiveImage: Record<string, unknown>;
  focalPoint?: { x: number; y: number };
}

interface Country {
  title: string;
  countryCode: string;
}

interface ImageAsset {
  img: {
    url: string;
    width?: number;
    height?: number;
    alt?: string;
  };
}

interface PageRelatedEntry {
  __typename: string;
  slug?: string;
  buttonStyle?: string;
  typeColor?: ColorWrapper;
  labelColor?: ColorWrapper;
  icon?: ImageAsset;
  iconColor?: ColorWrapper;
  label?: string;
  buttonLabel?: { label: string };
  linksToProductPage?: boolean;
  product?: {
    product: {
      __typename: string;
      slug: string;
      productTypeSpecific?: { slug: string };
    };
  };
  page?: { __typename: string; slug: string };
}

type SlideTextAlign =
  | "center"
  | "left"
  | "right"
  | "fixedCenter"
  | "default"
  | "featuredCenter";

interface BaseSlide {
  title: string;
  countryAvailability: boolean;
  listOfCountries: boolean;
  countries?: Country[];
  bgColor?: BackgroundColorField;
  textColor?: ColorWrapper;
  overlay?: string;
  textAlign?: SlideTextAlign;
  textFlexPosition?: string;
  addCallToAction: boolean;
  pageRelated: PageRelatedEntry[];
}

interface SliderFeaturedSlide extends BaseSlide {
  __typename: "SliderFeaturedRecord";
  description: string;
  featuredImage: ResponsiveImageData;
  addParallaxElements: boolean;
  parallaxAsset?: { url: string };
  descriptionColor?: ColorWrapper;
  imagePosition?: string;
  image: ResponsiveImageData;
}

interface SliderImageSlide extends BaseSlide {
  __typename: "SliderImageRecord";
  id: string;
  containerType?: "default" | "reduced" | string;
  description: string;
  imagePosition?: string;
  label?: string;
  useLabelBeforeTitle?: boolean;
  addTitleUnderline?: boolean;
  underlineColor?: ColorWrapper;
  customUnderlineAsset?: ImageAsset;
  addIconGallery?: boolean;
  gallery?: { id: string; url: string; width: number; height: number }[];
  galleryPosition?: string;
  image: ResponsiveImageData;
}

interface VideoData {
  muxAssetId: string;
  streamingUrl: string;
  mp4High: string;
  mp4Med: string;
  mp4Low: string;
  duration: number;
  framerate: number;
  thumbJpg: string;
  thumbPng: string;
  thumbGif: string;
}

interface SliderVideoSlide extends BaseSlide {
  __typename: "SliderVideoRecord";
  id: string;
  description: string;
  label?: string;
  useLabelBeforeTitle?: boolean;
  posterImage: ResponsiveImageData;
  video: {
    id: string;
    url: string;
    width: number;
    height: number;
    title: string;
    video: VideoData;
  };
}

type SliderSlide = SliderFeaturedSlide | SliderImageSlide | SliderVideoSlide;

interface ScrollIconConfig {
  scrollIconType?: string;
  iconColor?: ColorWrapper;
  iconBackgroundColor?: ColorWrapper;
  addCustomIconBackgroundElement?: boolean;
  iconBackgroundAsset?: ImageAsset;
  customScrollIcon?: ImageAsset;
  iconFeatured?: boolean;
}

interface SpacerConfig {
  id: string;
  spacerDirection?: string;
  spacerPosition?: "bottom" | "top" | "invert";
  customShape?: ImageAsset;
  addIconElement?: boolean;
  spacerIcon?: Record<string, unknown>;
  iconPosition?: string;
}

interface HeroSliderSection {
  id: string;
  name: string;
  addSpacer: boolean;
  spacerConfiguration?: SpacerConfig;
  heroLayout?: string;
  addScrollIcon: boolean;
  scrollIcon?: ScrollIconConfig;
  activeMarkerColor?: ColorWrapper;
  markersColor?: ColorWrapper;
  bgColor?: BackgroundColorField;
  customNavigationMarker?: ImageAsset;
  addSliderNavigationsMarkers: boolean;
  navigationMarkersType?: string;
  toggleSlideAutoplay?: boolean | null;
  slider: SliderSlide[];
}

interface SliderNav extends ButtonGroupProps {
  goToSlide: (slideIndex: number) => void;
}

export default function HeroSlider(data: HeroSliderSection) {
  const router = useRouter();
  const currentCountry = resolveCurrentCountry(router.query.country);
  const ignoreGeo = router.query.ignoregeo;

  const filteredSlides = data.slider.filter((entry: SliderSlide) =>
    isAvailableForCountry(
      {
        countryAvailability: entry.countryAvailability,
        listOfCountries: entry.listOfCountries,
        countries: entry.countries,
      },
      currentCountry,
      ignoreGeo
    )
  );

  const responsiveSlider = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 1,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 1,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 1,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
    },
  };

  const [playing, setPlaying] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef<Carousel | null>(null);

  const slideAutoplayEnabled =
    isCmsBooleanEnabled(data.toggleSlideAutoplay) && filteredSlides.length > 1;

  const slideCount = filteredSlides.length;

  useEffect(() => {
    if (!slideAutoplayEnabled || playing || slideCount <= 1) return;

    let intervalId: number | undefined;
    const startId = window.setTimeout(() => {
      intervalId = window.setInterval(() => {
        carouselRef.current?.next(1);
      }, AUTOPLAY_INTERVAL_MS);
    }, AUTOPLAY_INIT_DELAY_MS);

    return () => {
      window.clearTimeout(startId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [slideAutoplayEnabled, playing, slideCount]);

  const goToDotSlide = (index: number, goToSlide: (slideIndex: number) => void) => {
    if (slideAutoplayEnabled) {
      goToSlide(getCarouselIndexForSlide(index, slideCount));
    } else {
      goToSlide(index);
    }
    setActiveSlide(index);
  };

  const CustomDots = ({ goToSlide }: SliderNav) => {
    return (
      <>
        {!playing && filteredSlides.length > 1 && (
          <nav
            aria-label="Slider navigation"
            className={`absolute z-30 w-full lg:w-fit justify-end mx-auto right-6 lg:right-10 ${
              data.addSpacer ? "-bottom-2" : "bottom-4 lg:bottom-8"
            } flex flex-row gap-2 lg:gap-8`}
          >
            {filteredSlides.map((entry: SliderSlide, i: number) => (
              <GeoWrapper
                key={i}
                initial={entry.countryAvailability}
                country={entry?.countries}
                list={entry?.listOfCountries}
              >
                <NavigationSlider
                  onClick={() => goToDotSlide(i, goToSlide)}
                  currentSlide={activeSlide}
                  key={i}
                  index={i}
                  type={data.navigationMarkersType ?? "dotted"}
                  custom={data.customNavigationMarker}
                  color={data.markersColor?.color}
                  activeColor={data.activeMarkerColor?.color}
                  length={filteredSlides.length}
                  label={entry.title}
                  spacer={data.addSpacer}
                />
              </GeoWrapper>
            ))}
          </nav>
        )}
      </>
    );
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const executeScroll = () =>
    scrollRef.current?.scrollIntoView({
      block: "start",
      behavior: "smooth",
      inline: "end",
    });

  const togglePlay = () => {
    setPlaying(!playing);
  };

  const renderSliderImageSlide = (entry: SliderImageSlide) => {
    const isReduced = entry.containerType === "reduced";
    const isContain = entry.imagePosition === "Contain";
    const copyCard = (
      <SliderCopyCard
        title={entry.title}
        description={entry.description}
        label={[entry.useLabelBeforeTitle, entry.label]}
        color={entry.textColor?.color}
        layout={entry.textAlign}
        flex={entry.textFlexPosition ?? "end"}
        cta={[entry.addCallToAction, entry.pageRelated]}
        underline={[
          entry.addTitleUnderline,
          entry.customUnderlineAsset,
          entry.underlineColor,
        ]}
        hasSpacer={data.addSpacer}
        embedded={isReduced || isContain}
      />
    );

    if (!isReduced) {
      if (isContain) {
        return (
          <>
            {!playing && (
              <>
                <div className={styles.defaultContainStage}>
                  <figure
                    className={styles.defaultContainImageColMobile}
                    aria-hidden
                  >
                    <DefaultContainSlideImage data={entry.image} />
                  </figure>
                  <div
                    className={styles.defaultContainGradient}
                    style={
                      {
                        "--hero-bg-color":
                          resolvePortfolioBackgroundHex(entry.bgColor) ??
                          "#ffffff",
                      } as React.CSSProperties
                    }
                    aria-hidden
                  />
                  <div className={styles.defaultContainCopyCell}>
                    {copyCard}
                  </div>
                  <figure
                    className={styles.defaultContainImageColDesktop}
                    aria-hidden
                  >
                    <DefaultContainSlideImage data={entry.image} />
                  </figure>
                </div>
                {entry.addIconGallery && (
                  <IconGallery
                    layout={entry.galleryPosition ?? "tRight"}
                    data={entry.gallery}
                  />
                )}
              </>
            )}
          </>
        );
      }

      return (
        <>
          <div className={sliderFullBleedImageClass}>
            <FullBackgroundImageHero
              position={entry.imagePosition}
              data={entry.image}
            />
          </div>
          {!playing && (
            <>
              {entry.addIconGallery && (
                <IconGallery
                  layout={entry.galleryPosition ?? "tRight"}
                  data={entry.gallery}
                />
              )}
              {copyCard}
            </>
          )}
        </>
      );
    }

    return (
      <>
        {!isContain && (
          <div className={sliderFullBleedImageClass}>
            <FullBackgroundImageHero
              position={entry.imagePosition}
              data={entry.image}
            />
          </div>
        )}
        {!playing && (
          <>
            <div className={styles.reducedSliderStage}>
              {isContain && (
                <>
                  <figure
                    className={styles.reducedSliderImageColMobile}
                    aria-hidden={!isContain}
                  >
                    <FullBackgroundImage
                      data={entry.image}
                      position="Contain"
                    />
                  </figure>
                  <div
                    className={styles.reducedSliderGradient}
                    style={
                      {
                        "--hero-bg-color":
                          resolvePortfolioBackgroundHex(entry.bgColor) ??
                          "#ffffff",
                      } as React.CSSProperties
                    }
                    aria-hidden
                  />
                </>
              )}
              <div className={styles.reducedSliderLayout}>
                <div className={styles.reducedSliderInner}>
                  <div className={styles.reducedSliderCopyCell}>{copyCard}</div>
                  {isContain && (
                    <figure
                      className={styles.reducedSliderImageColDesktop}
                      aria-hidden={!isContain}
                    >
                      <FullBackgroundImage
                        data={entry.image}
                        position="Contain"
                      />
                    </figure>
                  )}
                  {!isContain && (
                    <figure
                      className={styles.reducedSliderEmptyCol}
                      aria-hidden
                    />
                  )}
                </div>
              </div>
            </div>
            {entry.addIconGallery && (
              <IconGallery
                layout={entry.galleryPosition ?? "tRight"}
                data={entry.gallery}
              />
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div className="relative pt-[90px] lg:pt-[75px]">
      <Carousel
        ref={carouselRef}
        containerClass={`${
          data.heroLayout === "tall" ? "min-h-[90dvh]" : "min-h-[500px]"
        } ${styles.Slider} ${
          playing ? `${styles.playing}` : `${styles.noPlaying}`
        }  relative  w-full transition-all duration-500 ease-in xs-[4rem] `}
        responsive={responsiveSlider}
        itemClass="h-full"
        arrows={false}
        autoPlay={false}
        infinite={slideAutoplayEnabled}
        afterChange={(_, state) =>
          setActiveSlide(
            getSlideIndexFromCarousel(
              state.currentSlide,
              slideCount,
              slideAutoplayEnabled
            )
          )
        }
        customButtonGroup={<CustomDots goToSlide={() => {}} />}
      >
        {filteredSlides.map((entry: SliderSlide, index: number) => (
          <GeoWrapper
            key={index}
            initial={entry?.countryAvailability}
            country={entry?.countries}
            list={entry?.listOfCountries}
          >
            <article
              style={resolveBackgroundStyle(entry?.bgColor)}
              className="h-full relative flex"
              aria-label={entry.title}
            >
              {entry.__typename === "SliderFeaturedRecord" && (
                <div className={sliderFullBleedImageClass}>
                  <FullBackgroundImageHero
                    position={entry.imagePosition}
                    data={entry.image}
                  />
                </div>
              )}

              {entry.overlay && !playing && (
                <OverlayShadow
                  format={entry.__typename}
                  data={entry.overlay}
                  color={imageOverlayColor}
                  layout={entry.textAlign}
                />
              )}

              {entry.__typename === "SliderFeaturedRecord" && (
                <div className="grid grid-cols-1 lg:grid-cols-5 grid-rows-[auto_1fr] lg:grid-rows-1 container mx-auto h-full relative z-10 gap-6 md:gap-0">
                  <header className="col-span-1 lg:col-span-3 flex items-center max-lg:pt-12 [&>header>div]:max-lg:pt-0">
                    <FeaturedCopyCard
                      parallaxImage={[
                        entry.addParallaxElements,
                        entry.parallaxAsset,
                      ]}
                      title={entry.title}
                      description={entry.description}
                      color={entry.textColor?.color}
                      descriptionColor={entry.descriptionColor?.color}
                      cta={[entry.addCallToAction, entry.pageRelated]}
                    />
                  </header>
                  <figure className="col-span-1 lg:col-span-2 relative">
                    <FullBackgroundImage
                      data={entry.featuredImage}
                      position="Contain"
                    />
                  </figure>
                </div>
              )}

              {entry.__typename === "SliderImageRecord" &&
                renderSliderImageSlide(entry)}

              {entry.__typename === "SliderVideoRecord" && (
                <>
                  <VideoHeroSlider
                    onClick={() => togglePlay()}
                    color={entry.textColor?.color.hex}
                    poster={entry.posterImage}
                    file={entry.video.video}
                    className=""
                  />
                  {!playing && (
                    <SliderCopyCard
                      title={entry.title}
                      description={entry.description}
                      label={[entry.useLabelBeforeTitle, entry.label]}
                      color={entry.textColor?.color}
                      layout={entry.textAlign}
                      flex={entry.textFlexPosition ?? "end"}
                      cta={[entry.addCallToAction, entry.pageRelated]}
                      underline={undefined}
                    />
                  )}
                </>
              )}
            </article>
          </GeoWrapper>
        ))}
      </Carousel>
      {data.addScrollIcon && (
        <div
          className={`absolute bottom-0 h-[60px] md:h-[45px] w-full left-0 right-0`}
          ref={scrollRef}
        >
          {!playing && (
            <PageScroll
              onClick={() => executeScroll()}
              spacer={data.addSpacer}
              type={data.scrollIcon?.scrollIconType ?? "arrow"}
              color={data.scrollIcon?.iconColor?.color}
              bg={[
                data.scrollIcon?.addCustomIconBackgroundElement,
                data.scrollIcon?.iconBackgroundAsset,
                data.scrollIcon?.iconBackgroundColor?.color,
              ]}
              custom={data.scrollIcon?.customScrollIcon?.img}
              featured={data.scrollIcon?.iconFeatured}
            />
          )}
        </div>
      )}
      {data.addSpacer && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[11]">
          <Shape
            shape={data.spacerConfiguration?.customShape}
            color={data.bgColor}
            direction={"heroSlider"}
            position={
              data.spacerConfiguration?.spacerPosition === "invert"
                ? undefined
                : data.spacerConfiguration?.spacerPosition || "bottom"
            }
            icon={[
              data.spacerConfiguration?.addIconElement,
              data?.spacerConfiguration?.spacerIcon,
            ]}
            iconPosition={data.spacerConfiguration?.iconPosition}
            invert={false}
          />
        </div>
      )}
    </div>
  );
}

export const fragment = `
    fragment HeroSliderFragment on SectionHeroSliderRecord{
      id
    name
    addSpacer
    spacerConfiguration {
      id
      spacerDirection
      spacerPosition
      customShape {
        img {
          alt
          url
          width
          height
        }
      }
      addIconElement
      spacerIcon {
        img {
          alt
          url
          width
          height
        }
      }
      iconPosition
    }
    heroLayout
    addScrollIcon
    scrollIcon {
      scrollIconType
      iconColor {
        color {
          hex
        }
      }
      iconBackgroundColor{
        color{
          hex
        }
      }
      addCustomIconBackgroundElement
      iconBackgroundAsset{
        img{
          url
        }
      }
      customScrollIcon{
        img{
          url
        }
      }
    iconFeatured
    }
    activeMarkerColor {
      color {
        hex
      }
    }
    markersColor {
      color {
        hex
      }
    }
    bgColor {
      ${legacyBgColorFields}
    }
    customNavigationMarker{
      img{
        url
        width
        height
        alt
      }
    }
    addSliderNavigationsMarkers
    navigationMarkersType
    toggleSlideAutoplay
    slider {
      ... on SliderFeaturedRecord{
        __typename
        title
        description
        countryAvailability
        listOfCountries
        countries {
          title
          countryCode
        }
        featuredImage {
          responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 2400 }, sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px") {
            ... responsiveImageFragment
          }
        }
        addParallaxElements
        parallaxAsset {
          url
        }
        addCallToAction
        pageRelated {
        ...pageRelatedFragment
        }
        bgColor {
          ${legacyBgColorFields}
        }
        textColor {
          color {
            hex
            cssRgb
          }
        }
        descriptionColor {
          color {
            hex
            cssRgb
          }
        }
  
        textFlexPosition
        textAlign
        overlay
        imagePosition
        image {
          responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 2400 }, sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px") {
            ... responsiveImageFragment
          }
        }
      }
      ... on SliderImageRecord {
        __typename
        id
        containerType
        title
        overlay
        textAlign
        textFlexPosition
        description
        imagePosition
        bgColor {
          ${sliderImageBgColorSelection}
        }
        textColor{
          color{
            hex
          }
        }
        label
        useLabelBeforeTitle
        addCallToAction
        pageRelated {
        ...pageRelatedFragment
        }
        addTitleUnderline
        underlineColor{
          color{
            hex
          }
        }
        customUnderlineAsset{
          img{
            url
            width
            height
          }
        }
        addIconGallery
        gallery {
          id
          url
          width
          height
        }
        galleryPosition
        countryAvailability
        listOfCountries
        countries {
          title
          countryCode
        }
        image {
          responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 2400 }, sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px") {
            ... responsiveImageFragment
          }
          focalPoint {
            y
            x
          }
        }
      }
      ... on SliderVideoRecord {
        __typename
        id
        title
        overlay
        textAlign
        textFlexPosition
        description
        bgColor {
          ${legacyBgColorFields}
        }
        textColor{
          color{
            hex
          }
        }
        label
        useLabelBeforeTitle
        addCallToAction
        pageRelated {
        ...pageRelatedFragment
        }
        posterImage{
          responsiveImage (imgixParams: { fit: crop, crop: focalpoint, ar: "4:2", auto: format, q: 85, w: 2400 }, sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px") {
            ...responsiveImageFragment
          }
        }
        video {
          id
          url
          width
          height
          title
          video{
            muxAssetId
            streamingUrl
            mp4High: mp4Url(res: high)
            mp4Med: mp4Url(res: medium)
            mp4Low: mp4Url(res: low)
            duration
            framerate
            thumbJpg: thumbnailUrl(format: jpg)
            thumbPng: thumbnailUrl(format: png)
            thumbGif: thumbnailUrl(format: gif)
          }
          }
        }
      }
    }
`;
